import forEach from 'lodash/forEach';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {v4 as uuidV4} from 'uuid';
import moment from 'moment';
import IconSearch from '@spothero/icons/search';
import IconTimes from '@spothero/icons/times';
import Button from '@spothero/ui/button';
import DatePicker from '@spothero/ui/date-picker';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import StorageUtils from '@spothero/utils/storage';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import withConfig from 'config/withConfig';
import {push} from 'router/router-actions';
import {routeToSearch} from 'router/router-utils';
import {Page} from 'page/utils';
import DateUtils from 'utils/date';
import SegmentUtils from 'utils/segment';
import EventListItem from './EventListItem';

class EventList extends Component {
    static propTypes = {
        displayDateFormat: PropTypes.string.isRequired,
        searchString: PropTypes.string,
        city: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        showEventList: PropTypes.bool,
        onToggleEventList: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
    };
    state = {
        date: '',
        name: '',
        key: uuidV4(),
    };

    componentDidMount() {
        const {
            searchString,
            destination: {title},
        } = this.props;
        const userLatLng = StorageUtils.get('sh-user-location', 'session');

        SegmentUtils.track({
            event: 'Event List Display',
            properties: {
                /* eslint-disable camelcase */
                ...(userLatLng && {user_location: userLatLng}),
                ...(searchString && {search_query: searchString}),
                destination: title,
                /* eslint-enable camelcase */
            },
        });
    }

    _onDateChange = selectedDate => {
        this.setState({
            date: selectedDate,
        });
    };

    _onInputChange = evt => {
        this.setState({
            name: evt.target.value,
        });
    };

    _onClearClick = evt => {
        this.setState({
            date: '',
            name: '',
            key: uuidV4(),
        });
    };

    _onEventSelected = ({id}) => {
        routeToSearch({
            method: this.props.pushTo,
            pageType: Page.EVENT,
            id,
        });
    };

    _getFilteredEvents() {
        const {displayDateFormat, destination} = this.props;
        const {date, name} = this.state;
        const events = [];
        const hasDateFilter = !isEmpty(date);
        const hasNameFilter = !isEmpty(name);
        let formattedDate = null;

        forEach(destination.events, event => {
            formattedDate = moment(event.starts).format(displayDateFormat);

            if (hasNameFilter && hasDateFilter) {
                if (
                    includes(event.title.toUpperCase(), name.toUpperCase()) &&
                    includes(formattedDate, date)
                ) {
                    events.push(event);
                }
            } else if (hasNameFilter) {
                if (includes(event.title.toUpperCase(), name.toUpperCase())) {
                    events.push(event);
                }
            } else {
                if (includes(formattedDate, date)) {
                    events.push(event);
                }
            }
        });

        return events;
    }

    render() {
        const {
            destination,
            city,
            showEventList,
            onToggleEventList,
        } = this.props;

        if (isEmpty(destination.events)) {
            return null;
        }

        const {key} = this.state;
        const classes = classNames('EventList', {
            'EventList-open': showEventList,
        });
        const events = this._getFilteredEvents();

        return (
            <div className={classes}>
                <Button className="EventList-close" onClick={onToggleEventList}>
                    <IconTimes />
                </Button>
                <div className="EventList-static">
                    <h2 className="heading-md">Find Parking for Events</h2>
                    <p>
                        Going to an event? Select your event for accurate
                        pricing and availability.
                    </p>
                    <div key={key} className="EventList-filters">
                        <DatePicker
                            id="EventList-event-date"
                            placeholder="Select Event Date"
                            iconPosition="left"
                            displayFormat={
                                DateUtils.getTransientDateDisplayFormat
                            }
                            onChange={this._onDateChange}
                        />
                        <TextInput
                            placeholder="Search Event Name"
                            icon={<IconSearch />}
                            onChange={this._onInputChange}
                        />
                    </div>
                </div>
                {events.length > 0 ? (
                    <div className="EventList-events">
                        {events.map(event => {
                            return (
                                <EventListItem
                                    key={event.id}
                                    event={event}
                                    destination={destination}
                                    city={city}
                                    onClick={this._onEventSelected}
                                    isMobile={false}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <p className="EventList-no-results">
                        No events match this search
                        <TextButton onClick={this._onClearClick}>
                            Clear Filters
                        </TextButton>
                    </p>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: cityData},
        destination: {data: destinationData},
        searchRequest: {search_string: searchString},
    } = state;

    return {
        city: cityData,
        destination: destinationData,
        searchString,
    };
};

const mapDispatchToProps = {
    pushTo: push,
};

const enhance = compose(
    withConfig(['displayDateFormat']),
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(EventList);
